import apiClient, { unwrapResponse } from './apiClient';
import type {
  TherapistDashboardStats,
  TherapistAppointment,
  TherapistProfile,
  TherapistEarnings,
  AvailabilitySlot,
  ClientSummary,
  TherapistNotification,
  ScheduleFilters,
} from '../types/therapist-dashboard.types';

// Backend DTO types for field mapping
interface BackendAvailabilitySlot {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

/**
 * Map backend availability slot to frontend format
 * Backend uses 'isActive', frontend uses 'isAvailable'
 */
function mapBackendSlotToFrontend(slot: BackendAvailabilitySlot): AvailabilitySlot {
  return {
    id: slot.id,
    therapistId: '', // Not provided by backend, component doesn't use it
    dayOfWeek: slot.dayOfWeek as AvailabilitySlot['dayOfWeek'],
    startTime: slot.startTime,
    endTime: slot.endTime,
    isAvailable: slot.isActive,
    recurring: true, // Backend only returns recurring slots
  };
}

/**
 * Map frontend availability slot to backend format
 */
function mapFrontendSlotToBackend(slot: AvailabilitySlot): BackendAvailabilitySlot {
  return {
    id: slot.id,
    dayOfWeek: slot.dayOfWeek,
    startTime: slot.startTime,
    endTime: slot.endTime,
    isActive: slot.isAvailable,
  };
}

export const therapistApi = {
  /**
   * Get therapist dashboard statistics
   */
  async getDashboardStats(): Promise<TherapistDashboardStats> {
    const response = await apiClient.get<{ success: boolean; data: TherapistDashboardStats }>(
      '/api/therapists/dashboard-stats'
    );
    return unwrapResponse(response);
  },

  /**
   * Get therapist's bookings/appointments
   */
  async getAppointments(filters?: ScheduleFilters): Promise<TherapistAppointment[]> {
    const response = await apiClient.get<{
      success: boolean;
      data: { appointments: TherapistAppointment[] };
    }>('/api/therapists/appointments', { params: filters });
    return response.data.data.appointments;
  },

  /**
   * Get therapist profile
   */
  async getProfile(): Promise<TherapistProfile> {
    const response = await apiClient.get<{ success: boolean; data: TherapistProfile }>(
      '/api/therapists/profile'
    );
    return unwrapResponse(response);
  },

  /**
   * Update therapist profile
   */
  async updateProfile(data: Partial<TherapistProfile>): Promise<TherapistProfile> {
    const response = await apiClient.patch<{ success: boolean; data: TherapistProfile }>(
      '/api/therapists/profile',
      data
    );
    return unwrapResponse(response);
  },

  /**
   * Get therapist earnings
   */
  async getEarnings(): Promise<TherapistEarnings> {
    const response = await apiClient.get<{ success: boolean; data: TherapistEarnings }>(
      '/api/therapists/earnings'
    );
    return unwrapResponse(response);
  },

  /**
   * Get therapist availability slots
   */
  async getAvailability(): Promise<AvailabilitySlot[]> {
    const response = await apiClient.get<{
      success: boolean;
      data: { slots: BackendAvailabilitySlot[] };
    }>('/api/therapists/availability');
    return response.data.data.slots.map(mapBackendSlotToFrontend);
  },

  /**
   * Update therapist availability
   */
  async updateAvailability(slots: AvailabilitySlot[]): Promise<AvailabilitySlot[]> {
    const backendSlots = slots.map(mapFrontendSlotToBackend);
    const response = await apiClient.post<{
      success: boolean;
      data: { slots: BackendAvailabilitySlot[]; message: string };
    }>('/api/therapists/availability', { slots: backendSlots });
    return response.data.data.slots.map(mapBackendSlotToFrontend);
  },

  /**
   * Get therapist's client list
   */
  async getClients(): Promise<ClientSummary[]> {
    const response = await apiClient.get<{
      success: boolean;
      data: { clients: ClientSummary[]; total: number };
    }>('/api/therapists/clients');
    return response.data.data.clients;
  },

  /**
   * Add or update notes for a client
   */
  async updateClientNotes(clientId: string, notes: string): Promise<void> {
    await apiClient.post('/api/therapists/client-notes', { clientId, notes });
  },

  /**
   * Get therapist notifications
   */
  async getNotifications(): Promise<TherapistNotification[]> {
    const response = await apiClient.get<{
      success: boolean;
      data: { notifications: TherapistNotification[]; unreadCount: number; total: number };
    }>('/api/therapists/notifications');
    return response.data.data.notifications;
  },

  /**
   * Mark notification as read
   */
  async markNotificationRead(notificationId: string): Promise<void> {
    await apiClient.post(`/api/therapists/notifications/${notificationId}/read`);
  },

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsRead(): Promise<void> {
    await apiClient.post('/api/therapists/notifications/read-all');
  },

  /**
   * Update appointment status (complete, no-show, etc.)
   */
  async updateAppointmentStatus(
    appointmentId: string,
    status: 'completed' | 'no_show' | 'cancelled'
  ): Promise<TherapistAppointment> {
    const response = await apiClient.post<{ success: boolean; data: TherapistAppointment }>(
      `/api/therapists/appointments/${appointmentId}/status`,
      { status }
    );
    return unwrapResponse(response);
  },
};
